import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { IoTrashOutline } from 'react-icons/io5';
import ReactDOM from 'react-dom';
import ProfileCard from '../../../components/ProfileCard';

// ProfileCardPortal component to render the profile card outside the card's DOM hierarchy
const ProfileCardPortal = ({ user, onClose }) => {
  // Create a div for the portal if it doesn't exist
  useEffect(() => {
    const profilePortalRoot = document.getElementById('profile-portal-root');
    if (!profilePortalRoot) {
      const div = document.createElement('div');
      div.id = 'profile-portal-root';
      document.body.appendChild(div);
    }
    
    // Clean up
    return () => {
      const profilePortalRoot = document.getElementById('profile-portal-root');
      if (profilePortalRoot && profilePortalRoot.childNodes.length === 0) {
        document.body.removeChild(profilePortalRoot);
      }
    };
  }, []);
  
  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/90 backdrop-blur-lg"
         style={{position: 'fixed', top: 0, left: 0, right: 0, bottom: 0}}
    >
      <div 
        className="absolute inset-0"
        onClick={() => {
          console.log('Backdrop clicked, closing profile card');
          onClose();
        }}
      ></div>
      
      <div 
        className="relative max-w-[90vw] z-[10000] bg-gradient-to-br from-[#1a1f36]/80 to-[#121524]/80 p-1 rounded-xl backdrop-blur-md shadow-2xl border border-soft-pink/30"
        onClick={(e) => e.stopPropagation()}
      >
        <ProfileCard 
          user={user}
          onClose={onClose}
          style={{ 
            maxWidth: '280px',
            width: '100%'
          }}
        />
      </div>
    </div>,
    document.getElementById('profile-portal-root') || document.body
  );
};

const ConnectionCard = ({ connection, onRemove }) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showProfileCard, setShowProfileCard] = useState(false);
  
  // Helper to get user details from connection object
  const getUserDetails = (connection) => {
    if (!connection) return { id: '', name: 'Unknown User', imageUrl: null };
    
    // Check if user details are directly in connection
    if (connection.name || connection.username) {
      return {
        id: connection.id || connection._id,
        name: connection.name || connection.username,
        imageUrl: connection.imageUrl || connection.profile_photo_url || connection.profilePhotoUrl || connection.avatar,
        location: connection.location || connection.city
      };
    }
    
    // Check if user details are in a nested 'user' object
    if (connection.user) {
      return {
        id: connection.user.id || connection.user._id,
        name: connection.user.name || connection.user.username,
        imageUrl: connection.user.imageUrl || connection.user.profile_photo_url || connection.user.profilePhotoUrl || connection.user.avatar,
        location: connection.user.location || connection.user.city
      };
    }
    
    // Check if the connection is to a connectedUser
    if (connection.connectedUser) {
      return {
        id: connection.connectedUser.id || connection.connectedUser._id,
        name: connection.connectedUser.name || connection.connectedUser.username,
        imageUrl: connection.connectedUser.imageUrl || connection.connectedUser.profile_photo_url || connection.connectedUser.profilePhotoUrl || connection.connectedUser.avatar,
        location: connection.connectedUser.location || connection.connectedUser.city
      };
    }
    
    // If no user details found, return the connection itself
    return {
      id: connection.id || connection._id || connection.connectionId,
      name: connection.name || connection.username || 'User',
      imageUrl: connection.imageUrl || connection.profile_photo_url || connection.profilePhotoUrl || connection.avatar,
      location: connection.location || connection.city
    };
  };

  const user = getUserDetails(connection);
  
  // Extract connection type
  const connectionType = (connection.connectionType || connection.type || 'friend').toLowerCase();

  const handleRemoveClick = (e) => {
    e.stopPropagation();
    setShowConfirmDialog(true);
  };

  const handleConfirmRemove = () => {
    onRemove(connection);
    setShowConfirmDialog(false);
  };

  const handleCancelRemove = () => {
    setShowConfirmDialog(false);
  };

  const handleViewProfile = () => {
    setShowProfileCard(true);
  };

  return (
    <motion.div
      whileHover={{ 
        y: -4,
        scale: 1.02,
        boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3)"
      }}
      whileTap={{ scale: 0.98 }}
      className="group overflow-hidden rounded-xl transition-all duration-300 h-full cursor-pointer"
      style={{ 
        background: `var(--pri-dark)`,
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}
      onClick={handleViewProfile}
    >
      <div className="p-4 relative overflow-hidden h-full">
        {/* Glossy effects with gradients matching Calendar */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
          <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
          <div className="absolute -left-10 -top-10 w-20 h-20 bg-soft-pink/20 rounded-full filter blur-[40px] opacity-30" />
          <div className="absolute -right-10 -bottom-10 w-20 h-20 bg-mint-green/20 rounded-full filter blur-[40px] opacity-20" />
          <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/20 to-transparent" />
        </div>
        
        <div className="flex items-center gap-3 relative z-10">
          {/* Profile Image or Initials */}
          <motion.div
            whileHover={{ scale: 1.05 }}
            transition={{ duration: 0.2 }}
            className="relative"
          >
            {user.imageUrl ? (
              <div className="w-12 h-12 rounded-full overflow-hidden shadow-lg border border-soft-pink/20 bg-gradient-to-br from-pri-dark/80 to-sec-dark/80">
                <img
                  src={user.imageUrl}
                  alt={user.name}
                  className="w-full h-full object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(user.name)}&background=random`;
                  }}
                />
              </div>
            ) : (
              <div className="w-12 h-12 rounded-full flex items-center justify-center bg-gradient-to-br from-soft-pink/20 to-mint-green/20 text-white shadow-lg border border-soft-pink/20">
                {user.name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
              </div>
            )}
          </motion.div>
          
          {/* User details */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-pri-white truncate">
              {user.name}
            </h3>
            <p className="text-sm text-pri-gray truncate">
              {connectionType.charAt(0).toUpperCase() + connectionType.slice(1)}
            </p>
          </div>
          
          {/* Remove button */}
          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleRemoveClick}
            className="p-2 rounded-full bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 border border-pri-white/10 hover:border-soft-pink/20 transition-all"
            aria-label="Remove connection"
          >
            <IoTrashOutline className="h-5 w-5 text-soft-pink" />
          </motion.button>
        </div>
      </div>

      {/* Global Confirmation Dialog Portal */}
      {showConfirmDialog && <ConfirmDialogPortal message={`Are you sure you want to remove ${user.name}?`} onConfirm={handleConfirmRemove} onCancel={handleCancelRemove} />}
      
      {/* Profile Card Portal */}
      {showProfileCard && (
        <ProfileCardPortal 
          user={{
            id: user.id,
            name: user.name,
            profilePhotoUrl: user.imageUrl,
            jobTitle: connectionType.charAt(0).toUpperCase() + connectionType.slice(1),
            location: user.location
          }}
          onClose={() => {
            console.log('Closing profile card');
            setShowProfileCard(false);
          }}
        />
      )}
    </motion.div>
  );
};

// Portal component to render the dialog outside of the card's DOM hierarchy
const ConfirmDialogPortal = ({ message, onConfirm, onCancel }) => {
  // Create a div for the portal if it doesn't exist
  useEffect(() => {
    const portalRoot = document.getElementById('portal-root');
    if (!portalRoot) {
      const div = document.createElement('div');
      div.id = 'portal-root';
      document.body.appendChild(div);
    }
    
    // Clean up
    return () => {
      const portalRoot = document.getElementById('portal-root');
      if (portalRoot && portalRoot.childNodes.length === 0) {
        document.body.removeChild(portalRoot);
      }
    };
  }, []);
  
  return ReactDOM.createPortal(
    <ConfirmationDialog message={message} onConfirm={onConfirm} onCancel={onCancel} />,
    document.getElementById('portal-root') || document.body
  );
};

// Redesigned confirmation dialog component
const ConfirmationDialog = ({ message, onConfirm, onCancel }) => {
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="fixed inset-0 bg-pri-dark bg-opacity-70" onClick={onCancel}></div>
      <motion.div 
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: "spring", damping: 25 }}
        className="z-10 w-full max-w-md mx-auto px-4"
      >
        <div className="relative bg-gradient-to-br from-sec-dark to-pri-dark rounded-2xl overflow-hidden shadow-2xl border border-soft-pink/10">
          {/* Glossy effects */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-soft-pink/10 via-transparent to-mint-green/5" />
            <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
            <div className="absolute -left-24 -top-24 w-48 h-48 bg-soft-pink/20 rounded-full filter blur-[80px] opacity-30" />
            <div className="absolute -right-24 -bottom-24 w-48 h-48 bg-mint-green/20 rounded-full filter blur-[80px] opacity-20" />
            <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-soft-pink/30 to-transparent" />
          </div>
          
          <div className="relative p-6 text-center">
            <h3 className="text-xl font-bold text-pri-white mb-4">Confirm Removal</h3>
            <p className="text-pri-gray mb-6">{message}</p>
            
            <div className="flex justify-between gap-4">
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                whileTap={{ scale: 0.95 }}
                onClick={onCancel}
                className="flex-1 py-2 px-4 rounded-xl bg-gradient-to-br from-pri-dark/60 to-sec-dark/60 text-pri-white border border-pri-white/10 hover:border-soft-pink/20"
              >
                Cancel
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05, boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)' }}
                whileTap={{ scale: 0.95 }}
                onClick={onConfirm}
                className="flex-1 py-2 px-4 rounded-xl bg-gradient-to-br from-soft-pink to-red-500 text-pri-dark font-medium shadow-lg"
              >
                Remove
              </motion.button>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default ConnectionCard; 